import { inject as service } from '@ember/service';
import { camelize } from '@ember/string';
import JSONAPIAdapter from '@ember-data/adapter/json-api';

import { pluralize } from 'ember-inflector';

import type SessionService from 'solomon-next-reporting-ui/services/session';
import type SolomonConfigService from 'solomon-next-reporting-ui/services/solomon-config';

export default class ApplicationAdapter extends JSONAPIAdapter {
  @service declare session: SessionService;
  @service declare solomonConfig: SolomonConfigService;

  namespace = 'api';

  get host(): string {
    const config = this.solomonConfig.config;

    return String(config.apis.solomonApi.host);
  }

  pathForType(modelName: string | number): string {
    const name = String(modelName);

    return pluralize(name.includes('-') ? camelize(name) : name);
  }

  get headers(): {} {
    const headers: Record<string, unknown> = {};

    if (this.session.isAuthenticated) {
      const { id: access_token } = this.session.data.authenticated;

      headers['Authorization'] = access_token;
    }

    return headers;
  }

  // The session check used to sit inside an `isInvalid` branch, but that hook
  // matches 422 alone, so it never ran on the status it was written for. Checked
  // here instead, before delegating.
  //
  // 403 is deliberately not included: the API uses it for permission denials as
  // well as authentication ones, and logging a user out because a single record
  // was off limits would be worse than the error itself.
  //
  // The rest of the override reproduced what JSONAPIAdapter already does with
  // isSuccess/isInvalid, so it has gone.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleResponse(status: number, headers: Headers, payload: any, requestData: { method: string; url: string }) {
    if (status === 401) {
      this.session.invalidate();
    }

    return super.handleResponse(status, headers, payload, requestData);
  }
}
